import { VoidCallback } from '@deqo/foundation';
import React from 'react';
import styled from 'styled-components';
import { LFC } from '../Legacy';
import { Button } from './Button';
import { Colors } from './Colors';
import { ImageFromCallback } from './ImageFrom';
import { Input } from './Input';
import { SpotLoader } from './SpotLoader';
import { View } from './View';

export type ImageUrlResolver = (url: string) => Promise<string | null>;

export class ImageFromUrl extends React.Component<{ resolver?: ImageUrlResolver, onSelect: ImageFromCallback, onConfirm: VoidCallback }> {

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    private tid: any | null = null;

    public state = {
        error: false,
        loading: false,
        value: '',
        validValue: '',
    };

    private get resolver() {
        return this.props.resolver ?? defaultImageUrlResolver;
    }

    public render() {
        return <View spacing="1">
            <View.Top>
                <Input
                    label="Enter an image URL:"
                    placeholder="e.g. https://i.imgur.com/yed5Zfk.gif"
                    error={this.state.error}
                    value={this.state.value}
                    onChange={this.onChange}
                />
            </View.Top>
            <View.Main>
                <Input.Label>Preview:</Input.Label>
                <PreviewBorder>
                    <Preview {...this.state} />
                </PreviewBorder>
            </View.Main>
            <View.Bottom>
                <View.Right>
                    <Button
                        label="Use Image"
                        variant="primary"
                        disabled={this.state.error || this.state.loading || !this.state.validValue}
                        onAction={this.props.onConfirm} />
                </View.Right>
            </View.Bottom>
        </View >;
    }

    private onChange = (value: string) => {
        this.setState({ value }, () => {
            if (value) {
                this.scheduleResolve();
            }
        });
    };

    private scheduleResolve = () => {
        clearTimeout(this.tid);
        this.tid = setTimeout(() => void (this.resolve()), 500);
    };

    private resolve = async () => {
        this.setState({ loading: true });
        const url = await this.resolver(this.state.value);
        const update = {
            loading: false,
            error: !url,
            validValue: url,
        };
        this.setState(update, () => {
            if (url) {
                this.props.onSelect({ url });
            }
        });
    };
}

const Preview: LFC<{ loading: boolean, validValue: string, value: string }> = ({ loading, validValue }) => {
    if (loading) return <SpotLoader color={Colors.gray300} />;
    if (!!validValue) {
        return <PreviewImage>
            <img src={validValue} />
        </PreviewImage>;
    }
    else {
        return <PreviewIcon />;
    }
};

const PreviewBorder = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    border-radius: 0.5rem;
    background-color: ${Colors.gray100};
    color: ${Colors.gray300};

    & > svg {
        flex: 0 0 auto;
        width: 10rem;
        height: 10rem;
    }
`;

const PreviewIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140 140">
        <g fill="currentColor">
            <path d="M140 17.5A17.5 17.5 0 00122.5 0h-105A17.5 17.5 0 000 17.5v105A17.5 17.5 0 0017.5 140h105a17.5 17.5 0 0017.5-17.5zm-11.67 105a5.83 5.83 0 01-5.83 5.83h-105a5.83 5.83 0 01-5.83-5.83v-105a5.83 5.83 0 015.83-5.83h105a5.83 5.83 0 015.83 5.83z" />
            <path d="M64.17 55.42a17.5 17.5 0 1035 0 17.5 17.5 0 10-35 0zM81.67 30.63a4.38 4.38 0 004.37-4.38v-2.92a4.38 4.38 0 00-8.75 0v2.92a4.38 4.38 0 004.38 4.38zM57.95 37.88a4.38 4.38 0 106.22-6.18l-2.06-2.07a4.39 4.39 0 00-6.22 6.2zM49.58 59.8h2.92a4.38 4.38 0 000-8.76h-2.92a4.38 4.38 0 000 8.75zM105.38 72.95a4.38 4.38 0 00-6.18 6.18l2.05 2.07a4.38 4.38 0 006.2-6.19zM106.46 55.42a4.38 4.38 0 004.37 4.37h2.92a4.38 4.38 0 000-8.75h-2.92a4.38 4.38 0 00-4.37 4.38zM102.29 39.17a4.33 4.33 0 003.09-1.29l2.06-2.06a4.38 4.38 0 00-6.19-6.19L99.2 31.7a4.38 4.38 0 003.09 7.47zM92.17 93.83a26.68 26.68 0 00-8.46 1.37 1.45 1.45 0 00-.7 2.26 45.66 45.66 0 018.86 21.32.94.94 0 00.93.8h18.4a8.96 8.96 0 007.37-3.86 26.87 26.87 0 00-26.4-21.89zM46.16 84.88a40.1 40.1 0 00-25.2 8.86 1.46 1.46 0 00-.54 1.16v15.75a8.95 8.95 0 008.95 8.93h55a1.46 1.46 0 001.12-.52 1.45 1.45 0 00.3-1.2 40.3 40.3 0 00-39.63-32.98z" />
        </g>
    </svg>;

const PreviewImage = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    & > img {
        display: block;
        max-width: 100%;
        max-height: 100%;
    }
`;

const defaultImageUrlResolver = async (url: string) => {
    return new Promise<string | null>((rs) => {
        const img = new Image();
        img.src = url;
        img.onerror = () => rs(null);
        img.onload = () => rs(url);
    });
};
